import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Dropdown,
  Menu,
  Modal,
  Input,
  Upload,
  Progress,
  message,
  Spin,
  Empty,
  Tooltip,
  Tag,
} from 'antd';
import {
  FilePdfOutlined,
  EyeOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  InboxOutlined,
  ReloadOutlined,
  ShareAltOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Swal from 'sweetalert2';
import {
  getProjectDocuments,
  uploadProjectDocument,
  renameProjectDocument,
  deleteProjectDocument,
} from '../../services/projectServices';
import ProjectDocumentPreviewModal from './modals/ProjectDocumentPreviewModal';

const ProjectDocuments = ({
  projectId,
  projectName,
  documents: initialDocuments = [],
  onDocumentsUpdated,
  onOpenUploadModal,
  onShareDocument,
}) => {
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState(initialDocuments);
  const [loading, setLoading] = useState(false);

  // Preview Modal
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  // Rename Modal
  const [renameDoc, setRenameDoc] = useState(null);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Upload Modal State
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [docName, setDocName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchedForProjectRef = React.useRef(null);

  useEffect(() => {
    if (projectId && fetchedForProjectRef.current !== projectId) {
      fetchedForProjectRef.current = projectId;
      fetchDocuments();
    }
  }, [projectId]);

  useEffect(() => {
    if (Array.isArray(initialDocuments)) {
      setDocuments(initialDocuments);
    }
  }, [initialDocuments]);

  const fetchDocuments = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const docs = await dispatch(getProjectDocuments(projectId));
      const validDocs = Array.isArray(docs) ? docs : [];
      setDocuments(validDocs);
      if (onDocumentsUpdated) {
        onDocumentsUpdated(validDocs);
      }
    } catch (err) {
      console.error('Failed to load project documents:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate Suggested Document Name matching mobile format
  const generateSuggestedName = (file = null) => {
    const todayDocCount = documents.filter((d) =>
      moment(d.created_at).isSame(moment(), 'day')
    ).length;
    const n = todayDocCount + 1;
    const dateStr = moment().format('MMM D, YYYY · h:mm A');
    return `Scan ${n} — ${dateStr}`;
  };

  const handleOpenUpload = () => {
    if (onOpenUploadModal) {
      onOpenUploadModal();
      return;
    }
    setSelectedFile(null);
    setDocName(generateSuggestedName());
    setUploadProgress(0);
    setUploadModalVisible(true);
  };

  const handleFileSelect = (file) => {
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      message.error('Please select a valid PDF file.');
      return false;
    }
    setSelectedFile(file);
    // Prefill name with file name without extension if scan name not customized
    const cleanFileName = file.name.replace(/\.[^/.]+$/, '');
    if (!docName || docName.startsWith('Scan ')) {
      setDocName(cleanFileName || generateSuggestedName());
    }
    return false; // prevent auto upload
  };

  const handleExecuteUpload = async () => {
    if (!selectedFile) {
      message.error('Please select a PDF file to upload');
      return;
    }
    if (!docName.trim()) {
      message.error('Please enter a document name');
      return;
    }

    setUploading(true);
    setUploadProgress(30);

    try {
      let detectedPages = 1;
      try {
        const text = await selectedFile.text();
        const matches = text.match(/\/Type\s*\/Page[^s]/gi);
        if (matches && matches.length > 0) {
          detectedPages = matches.length;
        } else {
          const countMatch = text.match(/\/Count\s+(\d+)/i);
          if (countMatch && countMatch[1]) {
            const p = parseInt(countMatch[1], 10);
            if (p > 0) detectedPages = p;
          }
        }
      } catch (e) {
        console.warn('Page detection failed:', e);
      }

      const formData = new FormData();
      const safeName = docName.trim();
      const filename = `${projectId}_${Date.now()}_${safeName}.pdf`;

      formData.append('file', selectedFile, filename);
      formData.append('name', safeName);
      formData.append('page_count', String(detectedPages));
      formData.append('project_id', String(projectId));

      setUploadProgress(70);
      await dispatch(uploadProjectDocument(projectId, formData));
      setUploadProgress(100);

      message.success('Document uploaded successfully');
      setUploadModalVisible(false);
      setSelectedFile(null);
      fetchDocuments();
    } catch (err) {
      console.error('Document upload error:', err);
      message.error(err?.response?.data?.message || err?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = (doc) => {
    setPreviewDoc(doc);
    setPreviewVisible(true);
  };

  const handleOpenRename = (doc) => {
    setRenameDoc(doc);
    setNewName(doc.name || doc.original_filename || '');
  };

  const handleSaveRename = async () => {
    if (!newName.trim()) {
      message.error('Document name cannot be empty');
      return;
    }
    setRenaming(true);
    try {
      await dispatch(renameProjectDocument(projectId, renameDoc.id, newName.trim()));
      message.success('Document renamed successfully');
      setRenameDoc(null);
      fetchDocuments();
    } catch (err) {
      console.error('Rename document error:', err);
      message.error('Failed to rename document');
    } finally {
      setRenaming(false);
    }
  };

  const handleDelete = (doc) => {
    Swal.fire({
      title: 'Move to Recycle Bin?',
      text: `"${doc.name || 'Document'}" will be moved to the Recycle Bin. You can restore it within 30 days.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0a497b',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Move to Recycle Bin',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await dispatch(deleteProjectDocument(projectId, doc.id));
          message.success('Document moved to Recycle Bin');
          fetchDocuments();
        } catch (err) {
          console.error('Delete document error:', err);
          message.error('Failed to delete document');
        }
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="project-documents-container" style={{ marginTop: 16, marginBottom: 24 }}>
      {/* Header Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
          padding: '14px 18px',
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          marginBottom: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
              fontSize: 18,
            }}
          >
            <FilePdfOutlined />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
              {projectName ? `${projectName} — Documents` : 'Project Documents'}
            </h4>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {projectName && (
                <span style={{ color: '#0038d1', fontWeight: 600 }}>{projectName} · </span>
              )}
              {documents.length} {documents.length === 1 ? 'document' : 'documents'} attached
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={fetchDocuments}
            disabled={loading}
            style={{ borderRadius: 8, fontWeight: 500 }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Content Area */}
      {loading && documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Loading documents..." />
        </div>
      ) : documents.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '50px 20px',
            background: '#ffffff',
            borderRadius: '12px',
            border: '2px dashed #cbd5e1',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: 26,
              color: '#94a3b8',
            }}
          >
            <FilePdfOutlined />
          </div>
          <h4 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
            No documents yet
          </h4>
          <p style={{ color: '#64748b', fontSize: 13, maxWidth: 400, margin: '0 auto 20px' }}>
            Upload contracts, sketches, inspection checklists, or paper documents as PDF for this project.
          </p>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenUpload}
            style={{ background: '#0a497b', borderColor: '#0a497b', borderRadius: 8 }}
          >
            Upload First Document
          </Button>
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {documents.map((doc) => {
            const docUrl = doc.source_url || doc.url || doc.document_url || '';
            const docNameStr = doc.name || doc.original_filename || 'Document';

            const cardMenu = (
              <Menu>
                <Menu.Item key="preview" icon={<EyeOutlined />} onClick={() => handlePreview(doc)}>
                  View Document
                </Menu.Item>
                <Menu.Item
                  key="share"
                  icon={<ShareAltOutlined />}
                  onClick={() => onShareDocument ? onShareDocument(doc) : handlePreview(doc)}
                >
                  Share Document
                </Menu.Item>
                <Menu.Item key="rename" icon={<EditOutlined />} onClick={() => handleOpenRename(doc)}>
                  Rename
                </Menu.Item>
                {docUrl && (
                  <Menu.Item
                    key="download"
                    icon={<DownloadOutlined />}
                    onClick={() => window.open(docUrl, '_blank')}
                  >
                    Download PDF
                  </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item
                  key="delete"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(doc)}
                >
                  Move to Recycle Bin
                </Menu.Item>
              </Menu>
            );

            return (
              <Col xs={24} sm={12} md={8} lg={6} key={doc.id}>
                <Card
                  hoverable
                  style={{
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease',
                  }}
                  bodyStyle={{ padding: 14 }}
                  onClick={() => handlePreview(doc)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    {/* PDF Badge Icon */}
                    <div
                      style={{
                        width: 44,
                        height: 48,
                        borderRadius: 8,
                        background: '#fef2f2',
                        border: '1px solid #fee2e2',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FilePdfOutlined style={{ color: '#dc2626', fontSize: 20 }} />
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: '#dc2626',
                          textTransform: 'uppercase',
                          marginTop: 2,
                          letterSpacing: 0.5,
                        }}
                      >
                        PDF
                      </span>
                    </div>

                    {/* Metadata & Title */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: '#0f172a',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: '1.3',
                          marginBottom: 4,
                        }}
                        title={docNameStr}
                      >
                        {docNameStr}
                      </div>

                      <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <span>
                          {doc.page_count ? `${doc.page_count} ${doc.page_count === 1 ? 'Page' : 'Pages'}` : '1 Page'}
                        </span>
                        {doc.file_size ? <span>· {formatFileSize(doc.file_size)}</span> : null}
                      </div>

                      <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4 }}>
                        {moment(doc.created_at).format('MMM D, YYYY')}
                      </div>
                    </div>

                    {/* 3-Dots Dropdown */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    >
                      <Dropdown
                        overlay={cardMenu}
                        trigger={['click']}
                        placement="bottomRight"
                        destroyPopupOnHide={true}
                        transitionName=""
                        getPopupContainer={() => document.body}
                      >
                        <Button
                          type="text"
                          icon={<MoreOutlined style={{ fontSize: 16, color: '#64748b' }} />}
                          style={{ width: 28, height: 28, padding: 0, touchAction: "manipulation" }}
                        />
                      </Dropdown>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* PDF Inline Preview Modal */}
      <ProjectDocumentPreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        document={previewDoc}
        onShare={
          onShareDocument
            ? () => {
                const doc = previewDoc;
                setPreviewVisible(false);
                onShareDocument(doc);
              }
            : undefined
        }
      />

      {/* Rename Document Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                border: '1px solid #bfdbfe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0038d1',
                fontSize: 16,
              }}
            >
              <EditOutlined />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.25 }}>
                Rename Document
              </div>
              <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 400, marginTop: 2 }}>
                Update the display title for this PDF document
              </div>
            </div>
          </div>
        }
        open={Boolean(renameDoc)}
        visible={Boolean(renameDoc)}
        onCancel={() => setRenameDoc(null)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
            <Button
              onClick={() => setRenameDoc(null)}
              disabled={renaming}
              style={{ borderRadius: 8, height: 38, fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSaveRename}
              loading={renaming}
              disabled={!newName.trim()}
              style={{
                background: !newName.trim() ? '#94a3b8' : '#0038d1',
                borderColor: !newName.trim() ? '#94a3b8' : '#0038d1',
                borderRadius: 8,
                height: 38,
                fontWeight: 600,
                padding: '0 18px',
              }}
            >
              Save Name
            </Button>
          </div>
        }
        width={480}
        destroyOnClose={true}
      >
        <div style={{ marginTop: 8 }}>
          {/* Current file badge */}
          {renameDoc && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 12,
              }}
            >
              <FilePdfOutlined style={{ color: '#dc2626', fontSize: 16 }} />
              <span
                style={{
                  fontWeight: 500,
                  color: '#334155',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {renameDoc.original_filename || renameDoc.name}
              </span>
              {renameDoc.file_size ? (
                <Tag style={{ margin: 0, fontSize: 10, background: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                  {(renameDoc.file_size / 1024).toFixed(0)} KB
                </Tag>
              ) : null}
            </div>
          )}

          {/* Visible Document Name Input */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', margin: 0 }}>
                Document Title / Name
              </label>
              <Tag color="blue" style={{ margin: 0, fontSize: 10, borderRadius: 4, padding: '0 6px', fontWeight: 600 }}>
                Required
              </Tag>
            </div>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onPressEnter={handleSaveRename}
              placeholder="Enter document title"
              maxLength={150}
              allowClear
              autoFocus
              prefix={<EditOutlined style={{ color: '#0038d1', marginRight: 6 }} />}
              style={{
                height: 44,
                borderRadius: 10,
                border: '1.5px solid #0038d1',
                fontSize: 14,
                fontWeight: 500,
                background: '#ffffff',
                boxShadow: '0 0 0 3px rgba(0, 56, 209, 0.08)',
                padding: '0 12px',
              }}
            />
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
              <InfoCircleOutlined style={{ color: '#94a3b8' }} />
              <span>Press Enter or click Save Name to update.</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectDocuments;
